// Image URL utilities for StoryfYC

// Default placeholder for missing/invalid covers - using a themed gradient placeholder
const DEFAULT_COVER_PLACEHOLDER = 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&h=600&fit=crop&q=80';

/**
 * Validate if a URL is a valid image URL
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid
 */
const isValidImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  // Check for common invalid patterns
  if (url.includes('undefined') || url.includes('null')) return false;
  if (url.includes('emergent.host') || url.includes('emergentagent.com')) {
    // These are ephemeral preview URLs - likely broken in production
    if (url.includes('/api/uploads/')) return false;
  }
  return true;
};

/**
 * Get a valid cover URL with fallback to placeholder
 * Handles:
 * - Missing/null covers
 * - Relative URLs from local storage
 * - Invalid/broken URLs from old environments
 * - Cloud storage URLs (pass-through)
 * 
 * @param {string} cover - The cover URL from database
 * @returns {string} - Valid absolute URL or placeholder
 */
export const getAbsoluteCoverUrl = (cover) => {
  // No cover provided
  if (!cover) return DEFAULT_COVER_PLACEHOLDER;
  
  // Already a valid absolute URL (cloud storage, external image)
  if (cover.startsWith('https://') && isValidImageUrl(cover)) {
    return cover;
  }
  
  // HTTP URLs (upgrade to HTTPS or use placeholder)
  if (cover.startsWith('http://')) {
    return DEFAULT_COVER_PLACEHOLDER;
  }
  
  // Relative URL from local storage - prepend backend URL.
  // Production backend (storyfyc.com) serves /api/uploads/* correctly, so we
  // simply prepend the configured backend URL in every environment.
  if (cover.startsWith('/api/uploads/')) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${backendUrl}${cover}`;
  }
  
  // Unknown format - use placeholder
  return DEFAULT_COVER_PLACEHOLDER;
};

/**
 * Get placeholder cover URL
 * @returns {string} - Placeholder URL
 */
export const getPlaceholderCover = () => DEFAULT_COVER_PLACEHOLDER;

export default { getAbsoluteCoverUrl, getPlaceholderCover };
